import { useEffect, useRef } from "react";
import ApexCharts from "apexcharts";

export default function FunnelChart({ 
  data, 
  categories, 
  title, 
  proposalValue, 
  formattedProposalValue,
  negotiationValue,
  contractWonValue,
  projectsValue,
  width = "100%",
  height = "350",
  onCategoryClick,
  onCategoryRightClick
}) {
  const chartRef = useRef(null);
  const rightClickHandledRef = useRef(false);

  useEffect(() => {
    // Define 5-color gradient for 5 categories
    const gradientColors = ["#39C0C3", "#A9CF38", "#DAE254", "#F4E53B", "#74C163"]; // top → bottom

    const options = {
      series: [
        {
          name: title || "Funnel Series",
          data,
        },
      ],
      chart: {
        type: 'bar',
        width,
        height,
        dropShadow: { enabled: false },
        toolbar: { show: false },
        zoom: { enabled: false },
        selection: { enabled: false },
        events: {
          dataPointSelection: function(event, chartContext, config) {
            // Handle bar click (left-click only)
            if (onCategoryClick) {
              let categoryIndex = config.dataPointIndex;
              
              if (categoryIndex !== null && categoryIndex !== undefined && categoryIndex >= 0 && categoryIndex < categories.length) {
                onCategoryClick(categories[categoryIndex], categoryIndex);
              }
            }
          }
        }
      },
      tooltip: {
        enabled: true,
        y: {
          formatter: function (val, { dataPointIndex, w }) {
            const category = w.globals.labels[dataPointIndex];
            
            const formatCurrency = (value) => {
              return new Intl.NumberFormat('id-ID', {
                style: 'currency',
                currency: 'IDR',
                minimumFractionDigits: 0,
                maximumFractionDigits: 0
              }).format(value);
            };
            
            let tooltipText = `${category}: ${val}`;
            
            // Add money value for Negotiation and Contract/Won
            if (category === 'Negotiation' && negotiationValue && negotiationValue > 0) {
              tooltipText += `<br/>Value: ${formatCurrency(negotiationValue)}`;
            } else if (category === 'Contract/Won' && contractWonValue && contractWonValue > 0) {
              tooltipText += `<br/>Value: ${formatCurrency(contractWonValue)}`;
            } else if (category === 'Proposal' && proposalValue && proposalValue > 0) {
              tooltipText += `<br/>Value: ${formatCurrency(proposalValue)}`;
            } else if (category === 'Project' && projectsValue && projectsValue > 0) {
              tooltipText += `<br/>Value: ${formatCurrency(projectsValue)}`;
            }
            
            return tooltipText;
          }
        }
      },
      plotOptions: {
        bar: {
          horizontal: true,
          distributed: true,
          barHeight: '60%',
          borderRadius: 4,
          isFunnel: true,
        },
      },
      dataLabels: {
        enabled: true,
        style: {
          colors: ['#000000'],
          fontSize: '14px',
          fontWeight: 600
        },
        formatter: function (val, opt) {
          const category = opt.w.globals.labels[opt.dataPointIndex];
          
          const formatCurrency = (value) => {
            return new Intl.NumberFormat('id-ID', {
              style: 'currency',
              currency: 'IDR',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(value);
          };
          
          // ✅ FIX: Show money prominently for Proposal
          if (category === 'Proposal' && proposalValue && proposalValue > 0) {
            return `${category}: ${formatCurrency(proposalValue)} (${val} proposals)`;
          }
          
          // ✅ FIX: Show money prominently for Negotiation
          if (category === 'Negotiation' && negotiationValue && negotiationValue > 0) {
            return `${category}: ${formatCurrency(negotiationValue)} (${val} proposals)`;
          }
          
          // ✅ FIX: Show money prominently for Contract/Won
          if (category === 'Contract/Won' && contractWonValue && contractWonValue > 0) {
            return `${category}: ${formatCurrency(contractWonValue)} (${val} proposals)`;
          }
          
          // ✅ FIX: Show money prominently for Project
          if (category === 'Project' && projectsValue && projectsValue > 0) {
            return `${category}: ${formatCurrency(projectsValue)} (${val} projects)`;
          }
          
          return `${category}: ${val}`;
        },
        dropShadow: { enabled: false },
      },
      xaxis: { 
        categories,
        labels: {
          style: {
            colors: '#000000'
          }
        }
      },
      yaxis: {
        labels: {
          style: {
            colors: '#000000'
          }
        }
      },
      legend: { 
        show: true,
        position: 'bottom',
        horizontalAlign: 'center',
        fontSize: '16px',
        labels: {
          colors: '#000000'
        },
        markers: {
          width: 12,
          height: 12,
          radius: 2
        }
      },
      colors: gradientColors,
      responsive: [
        {
          breakpoint: 768,
          options: { chart: { width: "100%", height: "300" }, legend: { position: "bottom" } },
        },
        {
          breakpoint: 480,
          options: { chart: { width: "100%", height: "250" }, legend: { position: "bottom" } },
        },
      ],
    };

    const chart = new ApexCharts(chartRef.current, options);
    chart.render();

    // SINGLE right-click handler using mousedown
    const handleRightClick = (e) => {
      if (e.button !== 2) return; // Only handle right mouse button
      if (!onCategoryRightClick) return;
      
      // Prevent multiple calls with ref
      if (rightClickHandledRef.current) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      
      rightClickHandledRef.current = true;
      setTimeout(() => {
        rightClickHandledRef.current = false;
      }, 500);
      
      e.preventDefault();
      e.stopPropagation();
      
      const target = e.target;
      if (!target || !chartRef.current) return;
      
      // Find category index from clicked element
      let categoryIndex = null;
      
      // Check if clicked on a bar
      const bar = target.closest('.apexcharts-bar-area, .apexcharts-bar');
      if (bar) {
        const allBars = chartRef.current.querySelectorAll('.apexcharts-bar-area');
        categoryIndex = Array.from(allBars).indexOf(bar);
      }
      
      // Check if clicked on y-axis label
      if (categoryIndex === null || categoryIndex === -1) {
        const yAxisTextsGroup = chartRef.current.querySelector('.apexcharts-yaxis-texts-g');
        if (yAxisTextsGroup && yAxisTextsGroup.contains(target)) {
          const yAxisLabels = yAxisTextsGroup.querySelectorAll('text');
          categoryIndex = Array.from(yAxisLabels).indexOf(target);
        }
      }
      
      // Execute callback if valid index found
      if (categoryIndex !== null && categoryIndex >= 0 && categoryIndex < categories.length) {
        onCategoryRightClick(categories[categoryIndex], categoryIndex);
      }
    };

    // Attach ONLY the mousedown handler
    if (onCategoryRightClick && chartRef.current) {
      chartRef.current.addEventListener('mousedown', handleRightClick, true);
      
      // Add cursor pointer to bars
      setTimeout(() => {
        if (chartRef.current) {
          const bars = chartRef.current.querySelectorAll('.apexcharts-bar-area');
          bars.forEach(bar => {
            bar.style.cursor = 'pointer';
          });
        }
      }, 500);
    }

    return () => {
      if (chartRef.current) {
        chartRef.current.removeEventListener('mousedown', handleRightClick, true);
      }
      chart.destroy();
    };
  }, [data, categories, title, proposalValue, negotiationValue, contractWonValue, projectsValue, width, height, onCategoryClick, onCategoryRightClick]);

  return <div ref={chartRef} className="funnel-chart-wrapper"></div>;
}
